/* ═══════════════════════════════════════════════════════════════
   TaskService — Task CRUD, assignments, comments, links, attachments,
   notifications via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type {
  Task,
  TaskAssignment,
  TaskComment,
  TaskNotification,
  TaskLink,
  TaskAttachment,
  TaskProjectLink,
} from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

const taskOpts: TableOpts = { jsonFields: ['tags'] };

export const TaskService = {
  // ─── Tasks ───
  listTasks: async (): Promise<Task[]> => cache.list<Task>('tasks', taskOpts),
  saveTask: async (task: Task): Promise<Task> => cache.save('tasks', task, taskOpts),
  /** ONE call — the server cascades the delete (subtasks, comments, links,
   *  files, assignments, project links, notifications). No client-side
   *  multi-step cleanup: this just makes sure none of those child tables'
   *  LOCAL caches keep showing a stale row the server already removed. */
  deleteTask: async (id: string): Promise<void> => {
    await cache.remove('tasks', id);
    cache.invalidateAndNotify('tasks'); // drops any cached subtasks of this id
    cache.invalidateAndNotify('task_assignments');
    cache.invalidateAndNotify('task_comments');
    cache.invalidateAndNotify('task_links');
    cache.invalidateAndNotify('task_attachments');
    cache.invalidateAndNotify('task_project_links');
    cache.invalidateAndNotify('task_notifications');
  },

  /** Archive or restore — a THREE-key partial update on purpose: the server
   *  reads an `archived_at` flip of three keys or fewer as an archive toggle
   *  and gates it on `tasks.archive`. Mirrors ModuleService.setArchived. */
  setTaskArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      'tasks',
      {
        archived_at: archived ? new Date().toISOString() : null,
        archived_by: archived ? userName || null : null,
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate('tasks');
    logActivity({
      action: archived ? 'task.archive' : 'task.restore',
      entityType: 'task',
      entityId: id,
    });
  },

  // ─── Task Assignments ───
  listTaskAssignments: async (taskId?: string): Promise<TaskAssignment[]> => {
    if (taskId) return cache.query<TaskAssignment>('task_assignments', { taskId });
    return cache.list<TaskAssignment>('task_assignments');
  },
  saveTaskAssignment: async (a: TaskAssignment): Promise<TaskAssignment> =>
    cache.save('task_assignments', a),
  deleteTaskAssignment: async (id: string): Promise<void> => cache.remove('task_assignments', id),

  // ─── Task Comments ───
  listTaskComments: async (taskId: string): Promise<TaskComment[]> => {
    return cache.query<TaskComment>('task_comments', { taskId });
  },
  saveTaskComment: async (c: TaskComment): Promise<TaskComment> => cache.save('task_comments', c),

  // ─── Task Notifications ───
  // Decision 27: notifications are created by the SERVER alone (assigned /
  // status change / comment, plus the assignment email) — the client no
  // longer inserts them. A client may only flip `read` on its own row.
  listTaskNotifications: async (memberId?: string): Promise<TaskNotification[]> => {
    if (memberId)
      return cache.query<TaskNotification>('task_notifications', { teamMemberId: memberId });
    return cache.list<TaskNotification>('task_notifications');
  },
  /** The ONLY write a client may make to a notification: flip `read` on
   *  its own row (server-enforced — ec2-api/task-permissions.js). A
   *  narrow partial UPDATE, never a full save. */
  markTaskNotificationRead: async (id: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate('task_notifications', { read: true }, [
      { col: 'firm_id', op: '=', val: firmId },
      { col: 'id', op: '=', val: id },
    ]);
    cache.invalidateAndNotify('task_notifications');
  },

  // ─── Task Links ───
  listTaskLinks: async (taskId: string): Promise<TaskLink[]> => {
    return cache.query<TaskLink>('task_links', { taskId });
  },
  saveTaskLink: async (l: TaskLink): Promise<TaskLink> => cache.save('task_links', l),
  deleteTaskLink: async (id: string): Promise<void> => cache.remove('task_links', id),

  // ─── Task Attachments ───
  listTaskAttachments: async (taskId: string): Promise<TaskAttachment[]> => {
    return cache.query<TaskAttachment>('task_attachments', { taskId });
  },
  saveTaskAttachment: async (a: TaskAttachment): Promise<TaskAttachment> =>
    cache.save('task_attachments', a),
  deleteTaskAttachment: async (id: string): Promise<void> => cache.remove('task_attachments', id),

  // ─── Task ↔ Project Links ───
  listTaskProjectLinks: async (taskId?: string): Promise<TaskProjectLink[]> => {
    if (taskId) return cache.query<TaskProjectLink>('task_project_links', { taskId });
    return cache.list<TaskProjectLink>('task_project_links');
  },
  saveTaskProjectLink: async (l: TaskProjectLink): Promise<TaskProjectLink> =>
    cache.save('task_project_links', l),
  deleteTaskProjectLink: async (id: string): Promise<void> =>
    cache.remove('task_project_links', id),

  // ─── File dialog (needs main process) ───
  openFileDialog: async (): Promise<{
    data: string;
    fileName: string;
    mimeType: string;
  } | null> => {
    return (window as any).api.openExpenseFileDialog();
  },
};
